// src/components/DeviceManagementModal.jsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

export default function DeviceManagementModal({ onClose }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDeviceId, setCurrentDeviceId] = useState(null);

  useEffect(() => {
    loadDevices();
  }, []);

  async function loadDevices() {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Get current device ID
      const deviceId = localStorage.getItem('deviceId');
      setCurrentDeviceId(deviceId);

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setDevices(userData.devices || []);
      }
    } catch (err) {
      console.error('Error loading devices:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleRemoveDevice(deviceId) {
    if (!window.confirm('Remove this device? You will need to log in again on this device.')) {
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) return;

      const updatedDevices = devices.filter(d => d.id !== deviceId);

      await updateDoc(doc(db, 'users', user.uid), {
        devices: updatedDevices,
        updatedAt: new Date()
      });

      setDevices(updatedDevices);

      // If user removed current device, force logout
      if (deviceId === currentDeviceId) {
        alert('You removed this device. You will be logged out.');
        await auth.signOut();
        window.location.reload();
      }
    } catch (err) {
      console.error('Error removing device:', err);
      alert('Failed to remove device: ' + err.message);
    }
  }

  function formatDate(timestamp) {
    if (!timestamp) return 'Unknown';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }

    return date.toLocaleString();
  }

  function getDeviceIcon(type) {
    switch (type) {
      case 'mobile': return '📱';
      case 'tablet': return '📲';
      case 'desktop': return '💻';
      default: return '🖥️';
    }
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
          <div className="text-center">Loading devices...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="border-b p-6 flex justify-between items-center dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold dark:text-white">Device Management</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Maximum 2 devices per user
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {devices.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No devices registered yet
            </div>
          ) : (
            <div className="space-y-3">
              {devices.map((device) => (
                <div
                  key={device.id}
                  className={`border rounded-lg p-4 flex items-start justify-between ${
                    device.id === currentDeviceId
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-start gap-3 flex-1">
                    <div className="text-3xl">{getDeviceIcon(device.type)}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium dark:text-white">
                          {device.name || 'Unknown Device'}
                        </h3>
                        {device.id === currentDeviceId && (
                          <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">
                            Current Device
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        <div><strong>Browser:</strong> {device.browser || 'Unknown'}</div>
                        <div><strong>OS:</strong> {device.os || 'Unknown'}</div>
                        <div><strong>Last Login:</strong> {formatDate(device.lastLogin)}</div>
                        <div><strong>Added:</strong> {formatDate(device.addedAt)}</div>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleRemoveDevice(device.id)}
                    className="ml-4 px-3 py-1 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded border border-red-300 dark:border-red-800"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {devices.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded dark:bg-blue-900/20 dark:border-blue-800">
              <p className="text-sm text-blue-700 dark:text-blue-400">
                💡 <strong>Tip:</strong> You have {devices.length}/2 devices registered. 
                {devices.length >= 2 && ' Remove a device to add a new one.'}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-6 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 py-2 px-4 rounded font-medium dark:text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
